import { ComponentType } from 'react';

import { IMPORT_URL_REGEX } from './retry-lazy-load.constant';

// Function to extract the module URL from a dynamic import
const extractImportUrl = (
  importFunction: () => Promise<unknown>
): string | null => {
  try {
    const fnString = importFunction.toString();
    const match = IMPORT_URL_REGEX.exec(fnString);
    return match ? match[1] : null;
  } catch (error) {
    console.error('Error extracting component URL:', error);
    return null;
  }
};

// Function to modify import URL for retries (adds cache-busting parameter)
const getRetryImportFunction = (
  importFunction: () => Promise<unknown>,
  retryCount: number
): (() => Promise<unknown>) => {
  const importUrl = extractImportUrl(importFunction);
  if (!importUrl || retryCount === 0) return importFunction;

  // Ensure the query parameter doesn't break the import
  const newUrl = `${importUrl}${importUrl.includes('?') ? '&' : '?'}_retry=${retryCount}-${Math.random().toString(36).substring(2)}`;

  return () => import(/* @vite-ignore */ newUrl);
};

export const attemptLoadComponent = async <T extends ComponentType<unknown>>(
  importFunction: () => Promise<{ default: T }>,
  maxRetryCount: number,
  retryDelayMs: number
): Promise<{ default: T }> => {
  let retryCount = 0;

  while (retryCount <= maxRetryCount) {
    try {
      const retryImport = getRetryImportFunction(importFunction, retryCount);
      return (await retryImport()) as { default: T };
    } catch (error) {
      retryCount++;
      if (retryCount > maxRetryCount) {
        console.error('Component failed to load after maximum retries.');
        throw error;
      }
      const delay = retryDelayMs * Math.pow(2, retryCount - 1);
      console.warn(
        `Retry ${retryCount}/${maxRetryCount} failed. Retrying in ${delay}ms...`
      );
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw new Error('Unexpected error in retry loop.');
};
